interface ReadonlyArrayConstructor {
  /**
   * Creates an array from an iterable object.
   * @param iterable An iterable object to convert to an array.
   */
  from<T>(iterable: Iterable<T> | ArrayLike<T>): readonly T[];

  /**
   * Creates an array from an iterable object.
   * @param iterable An iterable object to convert to an array.
   * @param mapfn A mapping function to call on every element of the array.
   * @param thisArg Value of 'this' used to invoke the mapfn.
   */
  from<T, U>(
    iterable: Iterable<T> | ArrayLike<T>,
    mapfn: (v: T, k: number) => U,
    thisArg?: any,
  ): readonly U[];

  /**
   * Returns a new array from a set of elements.
   * @param items A set of elements to include in the new array object.
   */
  of<T>(...items: T[]): readonly T[];

  new (arrayLength?: number): readonly any[];
  new <T>(arrayLength: number): readonly T[];
  new <T>(...items: T[]): readonly T[];
  (arrayLength?: number): readonly any[];
  <T>(arrayLength: number): readonly T[];
  <T>(...items: T[]): readonly T[];
  isArray(arg: any): arg is readonly any[] | any[];
  readonly prototype: readonly any[];

  [Symbol.species]: ReadonlyArrayConstructor;

  Typed<T>(): ReadonlyArrayTypedConstructor<T>;
}

interface ReadonlyArrayTypedConstructor<T> {
  /**
   * Creates an array from an iterable object.
   * @param iterable An iterable object to convert to an array.
   */
  from(iterable: Iterable<T> | ArrayLike<T>): readonly T[];

  /**
   * Creates an array from an iterable object.
   * @param iterable An iterable object to convert to an array.
   * @param mapfn A mapping function to call on every element of the array.
   * @param thisArg Value of 'this' used to invoke the mapfn.
   */
  from<U>(
    iterable: Iterable<U> | ArrayLike<U>,
    mapfn: (v: U, k: number) => T,
    thisArg?: any,
  ): readonly T[];

  /**
   * Returns a new array from a set of elements.
   * @param items A set of elements to include in the new array object.
   */
  of(...items: T[]): readonly T[];

  readonly [Symbol.species]: ReadonlyArrayTypedConstructor<T>;

  readonly prototype: ReadonlyArray<T>;

  new (arrayLength?: number): readonly T[];
  new (...items: T[]): readonly T[];
  (arrayLength?: number): readonly T[];
  (...items: T[]): T[];
  isArray(arg: any): arg is readonly any[] | any[];
}

export const ReadonlyArray: ReadonlyArrayConstructor = Object.assign(
  Object.setPrototypeOf(
    function ReadonlyArray(this: unknown, ...args: any) {
      if (new.target) {
        return Reflect.construct(Array, args, new.target);
      } else {
        return Reflect.apply(Array, this, args);
      }
    } as typeof Array & { [Symbol.species]: ReadonlyArrayConstructor },
    Array,
  ),
  {
    Typed<T>() {
      return Array as ReadonlyArrayTypedConstructor<T>;
    },
    prototype: Array.prototype,
  },
);
